import {PlausibleCartSource, debounce, plausible_event} from '@bitstillery/common/lib/utils'
import {$t, api, logger, notifier} from '@bitstillery/common/app'
import {PlausibleEvent} from '@bitstillery/common/types/utils'
import {CartValidation, OfferItemFilterType, PricePreference} from '@bitstillery/common/api/fact2server'
import {is_one_of, merge_deep, present} from '@bitstillery/common/lib/utils'
import {as_state} from '@bitstillery/common/lib/unchecked'

import type {
    CreateEntityResponse,
    GetPortalOfferItemResponse,
    ModifyCartItemRequest,
    OkResponse,
} from '@bitstillery/common/api/fact2server'
import type {State} from '@bitstillery/mithril'

import type {CartValidationError} from '@/lib/state'
import {trigger_cart_bounce} from '@/lib/cart_bounce'
import {$m, $s} from '@/app'

// A timeout id to keep track of highlighted basket item state.
let CART_CHANGED: ReturnType<typeof setTimeout> | null = null
type CartWriteDebounce = ReturnType<typeof debounce<[CartItemWrite], Promise<CartUpdateStatus>>>
// Keyed by case artkey.
let CART_API_DEBOUNCES: Record<number, CartWriteDebounce> = {}
// The cart endpoints answer GONE when the order exists, but was confirmed and is no longer a cart.
const ORDER_GONE = 410
// A cart change is written a second after the last click (CART_API_DEBOUNCES below), so until then
// the customer's amount lives in this tab alone. These are the lines still waiting for their write;
// reloading the order while one is outstanding would throw that change away. Keyed by line because
// the debounce collapses clicks: one entry per line survives any number of them.
const CART_LINES_AWAITING_WRITE = new Set<number>()

export const VALIDATION_CATEGORIES = {
    PRICE: [CartValidation.PRICE_WAS_RAISED],
    QUANTITY: [CartValidation.QUANTITY, CartValidation.MIN_QUANTITY, CartValidation.STEP_SIZE],
}

/** The quantity validation error on a cart line, with the category it's filed under. */
function quantity_error(case_artkey: number): [CartValidation, CartValidationError] | undefined {
    for (const category of VALIDATION_CATEGORIES.QUANTITY) {
        const error = $s.cart.errors[category][case_artkey]
        if (error) {
            return [category, error]
        }
    }
    return undefined
}

export enum CartUpdateAction {
    CART_ADD = PlausibleEvent.CART_ADD,
    CART_DECREASE = PlausibleEvent.CART_DECREASE,
    CART_INCREASE = PlausibleEvent.CART_INCREASE,
    CART_MODIFY = 'cart_modify',
    CART_REMOVE = PlausibleEvent.CART_REMOVE,
    CART_UNCHANGED = 'cart_unchanged',
}
export interface CartUpdateStatus {
    action: CartUpdateAction
    /** Undefined when a removed line never had its amount confirmed by the backend. */
    amount: number | undefined
}

export const VALIDATION_ICONS = {
    [CartValidation.QUANTITY]: 'stock',
    [CartValidation.MIN_QUANTITY]: 'trolly',
    [CartValidation.PRICE_WAS_RAISED]: 'money',
    [CartValidation.STEP_SIZE]: 'trolly',
}

/** A cart line: the offer item's fields as the API sends them, plus what the cart itself keeps. */
export type CartItemState = Pick<
    GetPortalOfferItemResponse,
    | 'bottle_alcohol_percentage'
    | 'bottle_refill_status'
    | 'bottle_volume'
    | 'case_artkey'
    | 'case_customs_status'
    | 'case_gift_box_type'
    | 'case_number_of_bottles'
    | 'custom_offer_item_artkey'
    | 'excise_per_case'
    | 'offer_item_type'
    | 'product_category'
    | 'product_name'
    | 'vat_percentage'
> & {
    /** Set by `update_cart` while a line at zero cases waits for its delete. */
    _invisible?: boolean
    /** The portal order item's artkey, once the backend has one. */
    artkey: number | null
    /** Confirming a raised price writes the validated number over the API's string. */
    list_price: string | number | null
    /** Set from a MIN_QUANTITY validation error. */
    minimum_quantity?: number | null
    /** The number of cases as being known by the frontend. */
    number_of_cases: number
    /** The number of cases as being known by the backend. This lags behind the state of the frontend.*/
    number_of_cases_api?: number
    offer_item_artkey: number | null
    /** Only a line that came back from the backend has one. */
    portal_comment?: string | null
    /** Set from a STEP_SIZE validation error. */
    step_size_quantity?: number | null
    /** Only a line added in this tab has one; a line loaded with the order doesn't. */
    unit?: PricePreference
    value: number
}

/**
 * An offer item as the cart keeps it: confirming a raised price writes the validated number over the API's string.
 * The CMS product carousel stores its rows here too, and a public pallet-deal row names its offer item in
 * `offer_item_artkey`. The favourites list's rows come in as well, with when their update was seen.
 */
export type CartOfferItem = Omit<GetPortalOfferItemResponse, 'bottle_gtin_code' | 'list_price'> & {
    /** A pallet-deal row carries its GTINs as `bottle_gtin_codes` and has none under this name. */
    bottle_gtin_code?: string[]
    /** Only a favourite has one. */
    created_on?: string
    list_price: string | number
    offer_item_artkey?: number
    /** Only a public pallet-deal row has one; the guest card shows it in place of the product photo. */
    product_category_image_location?: string | null
    /** Only a favourite has one. */
    update_seen_on?: string | null
}

/** `$s.cart.items` read by a case artkey that may not be in the cart. */
export type CartItemLookup = Record<number, State<CartItemState> | undefined>
/** `$s.cart.offer_items` read by a case artkey whose offer item may be gone. */
export type CartOfferItemLookup = Record<number, State<CartOfferItem> | undefined>

/**
 * Add the state of a new cart item to the store.
 * The attributes here are expected to come either from an
 * offer_item (in case when an offer_item is added to the state) or
 * from a portal_order_item, in case the order is reloaded.
 */
export function add_cart_item_state(cart_item_state: CartItemState, initialize = false) {
    if (cart_item_state.number_of_cases) {
        // A portal_order_item has number_of_cases
        if ($s.identity.user.price_preference === PricePreference.Case) {
            cart_item_state.value = cart_item_state.number_of_cases
        } else {
            cart_item_state.value = cart_item_state.number_of_cases * cart_item_state.case_number_of_bottles
        }

        if (initialize) {
            cart_item_state.number_of_cases_api = cart_item_state.number_of_cases
        }
    } else {
        if ($s.identity.user.price_preference === PricePreference.Case) {
            cart_item_state.number_of_cases = cart_item_state.value
        } else {
            cart_item_state.number_of_cases = cart_item_state.value / cart_item_state.case_number_of_bottles
        }
        if (initialize) {
            cart_item_state.number_of_cases_api = cart_item_state.number_of_cases
        }
    }

    $s.cart.items[cart_item_state.case_artkey] = as_state(cart_item_state)
}

export function booked_amount() {
    return [...$s.cart.sales_order_items, ...$s.cart.sales_order_tbo_items]
        .map((i) => i.number_of_cases * Number(i.price_per_case))
        .reduce((a, b) => a + b, 0)
}

export function booked_bottles() {
    return [...$s.cart.sales_order_items, ...$s.cart.sales_order_tbo_items]
        .map((i) => i.number_of_cases * i.case_number_of_bottles)
        .reduce((a, b) => a + b, 0)
}

export function booked_cases() {
    return [...$s.cart.sales_order_items, ...$s.cart.sales_order_tbo_items]
        .map((i) => i.number_of_cases)
        .reduce((a, b) => a + b, 0)
}

export function cart_amount(validate = false) {
    return Object.values($s.cart.items)
        .map((i) => {
            let quantity = i.number_of_cases
            let price = i.list_price

            if (validate) {
                // Check for quantity validation errors
                const line_quantity_error = quantity_error(i.case_artkey)?.[1]
                if (line_quantity_error) {
                    quantity = line_quantity_error.valid_value
                }

                // Check for price validation errors
                const price_errors: Record<string, CartValidationError | undefined> =
                    $s.cart.errors[CartValidation.PRICE_WAS_RAISED]
                const price_error = price_errors[i.case_artkey]
                if (price_error) {
                    price = price_error.valid_value
                }
            }

            return quantity * Number(price)
        })
        .reduce((a, b) => a + b, 0)
}

export const cart_amount_validated = () => cart_amount(true)

function cart_quantity_base(as_bottles: boolean, validated: boolean) {
    return Object.values($s.cart.items)
        .map((i) => {
            let cases = i.number_of_cases

            if (validated) {
                const line_quantity_error = quantity_error(i.case_artkey)?.[1]
                if (line_quantity_error) {
                    cases = line_quantity_error.valid_value
                }
            }

            return as_bottles ? cases * i.case_number_of_bottles : cases
        })
        .reduce((a, b) => a + b, 0)
}

export const cart_bottles = (validated = false) => cart_quantity_base(true, validated)
export const cart_cases = (validated = false) => cart_quantity_base(false, validated)
export const cart_bottles_validated = () => cart_quantity_base(true, true)
export const cart_cases_validated = () => cart_quantity_base(false, true)
export const cart_units = () => ($s.cart.show_bottles ? cart_bottles() : cart_cases())

export async function confirm_all_suggestions() {
    const fix_promises: Promise<CartUpdateStatus>[] = []

    for (const error_category of error_categories()) {
        const errors: Record<string, CartValidationError> = $s.cart.errors[error_category]
        for (const [case_artkey, error_value] of Object.entries(errors)) {
            const case_artkey_num = Number(case_artkey)
            // The order can hold a line this tab doesn't know about yet.
            const cart_items: CartItemLookup = $s.cart.items
            const cart_item = cart_items[case_artkey_num]
            if (!cart_item) {
                continue
            }

            const payload: CartItemUpdate = {}
            if (error_category === CartValidation.PRICE_WAS_RAISED) {
                // Update the cart item list price, so the VAT/Total calculations are correct again.
                // Note that the difference between the old cart item list price and the new one
                // In the offeritem/validation valid_value, is used to show before/after prices in the UI.
                cart_item.list_price = error_value.valid_value
                payload.price_per_case = error_value.valid_value
            } else {
                payload.number_of_cases = error_value.valid_value
            }
            fix_promises.push(update_cart(payload, case_artkey_num, false))
        }
    }

    await Promise.all(fix_promises)
    // Clear validation errors.
    for (const error_category of error_categories()) {
        for (const case_artkey of Object.keys($s.cart.errors[error_category])) {
            delete $s.cart.errors[error_category][case_artkey]
        }
    }
    // Make sure the cart prices and stock status are updated.
    await update_cart_offer_items(false)
}

export async function confirm_suggestion_price_up(case_artkey: number) {
    const price_errors: Record<string, CartValidationError | undefined> = $s.cart.errors[CartValidation.PRICE_WAS_RAISED]
    const error = price_errors[case_artkey]
    if (!error) {
        throw new Error(`No quantity error found for case ${case_artkey}`)
    }
    const cart_item = present($s.cart.items[case_artkey], 'cart item')
    // Update the cart item list price, so the VAT/Total calculations are correct again.
    cart_item.list_price = error.valid_value
    const cart_update_status = await update_cart(
        {
            offer_item_artkey: cart_item.offer_item_artkey,
            price_per_case: error.valid_value,
        },
        case_artkey,
        false,
    )
    const offer_items: CartOfferItemLookup = $s.cart.offer_items
    const offer_item = offer_items[case_artkey]
    if (offer_item) {
        offer_item.list_price = error.valid_value
    }

    plausible_event(cart_update_status.action, {
        props: {
            alcohol: cart_item.bottle_alcohol_percentage,
            cases: cart_update_status.amount,
            company_type: $s.identity.user.company_type,
            customs_status: cart_item.case_customs_status,
            product_category: cart_item.product_category,
            product_name: cart_item.product_name,
            source: PlausibleCartSource.CART_FIX,
            volume: cart_item.bottle_volume,
        },
        revenue: {
            amount: Math.round(Number(cart_update_status.amount) * cart_item.list_price * 100) / 100,
            currency: $s.identity.user.currency,
        },
    })
    delete $s.cart.errors[CartValidation.PRICE_WAS_RAISED][case_artkey]
    notifier.notify($t('cart.new_price_confirmed'))
}

export async function confirm_suggestion_quantity(case_artkey: number) {
    const line_quantity_error = quantity_error(case_artkey)
    if (!line_quantity_error) {
        throw new Error(`Quantity error category not found for case ${case_artkey}`)
    }
    const [quantity_error_category, error] = line_quantity_error
    const cart_item = present($s.cart.items[case_artkey], 'cart item')
    // There may not be an offer_item if the product is unavailable; that's
    // We use the cart_item.offer_item_artkey instead.
    const cart_update_status = await update_cart(
        {
            number_of_cases: error.valid_value,
            offer_item_artkey: cart_item.offer_item_artkey,
        },
        case_artkey,
        false,
    )

    plausible_event(cart_update_status.action, {
        props: {
            alcohol: cart_item.bottle_alcohol_percentage,
            cases: cart_update_status.amount,
            company_type: $s.identity.user.company_type,
            customs_status: cart_item.case_customs_status,
            product_category: cart_item.product_category,
            product_name: cart_item.product_name,
            source: PlausibleCartSource.CART_FIX,
            volume: cart_item.bottle_volume,
        },
        revenue: {
            amount: Math.round(Number(cart_update_status.amount) * Number(cart_item.list_price) * 100) / 100,
            currency: $s.identity.user.currency,
        },
    })

    delete $s.cart.errors[quantity_error_category][case_artkey]
    const count = error.valid_value
    notifier.notify(
        $t('cart.new_quantity_confirmed', {
            count,
            product: cart_item.product_name,
            units: $t(`unit.${$s.identity.user.price_preference}`, {count}),
        }),
    )
}

/** The validation categories `$s.cart.errors` holds, in its order; the state proxy lists only data keys. */
export function error_categories(): CartValidation[] {
    return Object.keys($s.cart.errors).filter((key) => is_one_of(Object.values(CartValidation), key))
}

export function has_errors() {
    return error_categories().some((error_category) => Object.keys($s.cart.errors[error_category]).length > 0)
}

export function reset_quantity_validation(case_artkey: number) {
    logger.debug('[cart] reset cart validation state')
    delete $s.cart.errors[CartValidation.QUANTITY][case_artkey]
    delete $s.cart.errors[CartValidation.MIN_QUANTITY][case_artkey]
    delete $s.cart.errors[CartValidation.STEP_SIZE][case_artkey]
}

export function reset_validation() {
    for (const error_category of error_categories()) {
        for (const case_artkey of Object.keys($s.cart.errors[error_category])) {
            delete $s.cart.errors[error_category][case_artkey]
        }
    }
}

export function set_cart_marker(case_artkey: number) {
    if (CART_CHANGED) {
        clearTimeout(CART_CHANGED)
    }
    CART_CHANGED = setTimeout(() => {
        $s.cart.item_changed = null
    }, 3000)
    // Variable used to highlight newly added items.
    $s.cart.item_changed = case_artkey
}

export const total_amount = (validate = false) => total_amount_no_vat(validate) + vat_total(validate)
export const total_amount_validated = () => total_amount(true)

export function total_amount_no_vat(validate = false) {
    let total = cart_amount(validate) + booked_amount()
    const voucher = voucher_additional()
    if (voucher?.total) {
        total += Number(voucher.total)
    }

    return total
}

/**
 * Endpoint:
 *  - order_artkey (int): Sales Order to add the item to; must already exist (established by
 *    load_current_order on boot/login — the v2 endpoint does not create an order on the fly).
 *  - offer_item_artkey (int): Offer item artkey.
 *  - number_of_cases (int): Number of cases to add.
 *  - price_per_case (numeric): Price per case as shown to the customer. Might include excise.
 *  - excise_per_case (numeric): Excise per case for this case.
 *  - portal_comment (str): Comment for this item.
 */
async function update_cart_item_api(cart_api_data: CartItemWrite): Promise<CartUpdateStatus> {
    $s.cart.loading = true
    // Defensive: the order should already be established (load_current_order runs on boot and
    // on login). Without one there is nothing to write the line to, so the write fails and the
    // current order is reloaded below, rather than the cart getting stuck on orders/null/*.
    const order_artkey = $s.cart.artkey
    let status: CartUpdateStatus = {action: CartUpdateAction.CART_UNCHANGED, amount: cart_api_data.number_of_cases}

    try {
        if (!order_artkey) {
            throw new Error('cart has no order to write to')
        }
        const endpoint = `portal/orders/${order_artkey}/portal-order-items`

        if (cart_api_data.number_of_cases <= 0) {
            const portal_order_item_artkey = present($s.cart.items[cart_api_data.case_artkey], 'cart item').artkey
            if (!portal_order_item_artkey) {
                // Removed within the debounce of its add, so the line was never written.
                delete $s.cart.items[cart_api_data.case_artkey]
                return {action: CartUpdateAction.CART_UNCHANGED, amount: 0}
            }
            const {status_code, success} = await api.delete(`${endpoint}/${portal_order_item_artkey}`)

            if (status_code === ORDER_GONE) {
                await $m.order.reload_current_order()
                return {action: CartUpdateAction.CART_UNCHANGED, amount: 0}
            }
            if (!success) {
                throw new Error(`cart delete api failed for item ${portal_order_item_artkey}`)
            }

            const current_amount = present($s.cart.items[cart_api_data.case_artkey], 'cart item').number_of_cases_api
            logger.debug(`[cart] delete item ${cart_api_data.case_artkey} from cart state (${String(current_amount)})`)
            delete $s.cart.items[cart_api_data.case_artkey]
            return {action: CartUpdateAction.CART_REMOVE, amount: current_amount}
        }

        // The route declares only OkResponse, but a new line answers 201 with its artkey.
        const {result, status_code, success} = await api.post<CreateEntityResponse | OkResponse>(endpoint, cart_api_data, true)

        if (status_code === ORDER_GONE) {
            // Validating the order the item never landed on is pointless; reload instead.
            await $m.order.reload_current_order()
            return {action: CartUpdateAction.CART_UNCHANGED, amount: cart_api_data.number_of_cases}
        }
        await validate_cart()

        if (!success) {
            throw new Error(`cart update api failed: ${JSON.stringify(result)}`)
        }

        logger.info(`[cart] update api: ${cart_api_data.case_artkey}/${cart_api_data.number_of_cases}`)

        const cart_item = present($s.cart.items[cart_api_data.case_artkey], 'cart item')
        if (!cart_item.artkey) {
            status = {action: CartUpdateAction.CART_ADD, amount: cart_api_data.number_of_cases}
            if ('artkey' in result) {
                cart_item.artkey = result.artkey
            }
            cart_item.number_of_cases_api = cart_api_data.number_of_cases
        } else {
            // A line the backend never confirmed has no API amount; as NaN it matches neither comparison.
            const current_amount = Number(cart_item.number_of_cases_api)
            const new_amount = cart_item.number_of_cases

            if (current_amount > new_amount) {
                status = {action: CartUpdateAction.CART_DECREASE, amount: current_amount - new_amount}
            } else if (current_amount < new_amount) {
                status = {action: CartUpdateAction.CART_INCREASE, amount: new_amount - current_amount}
            } else {
                // This is not a Plausable event (yet).
                status = {action: CartUpdateAction.CART_MODIFY, amount: new_amount}
            }
            // Update the API value to the new amount; we already know what changed by now.
            cart_item.number_of_cases_api = new_amount
        }
    } catch {
        notifier.notify($t('notifications.cart_api_error'), 'danger', 20_000)
        status = {action: CartUpdateAction.CART_UNCHANGED, amount: cart_api_data.number_of_cases}
    } finally {
        CART_LINES_AWAITING_WRITE.delete(cart_api_data.case_artkey)
    }

    if (!order_artkey) {
        // Keep loading=true across the reload so the checkout guard keeps suppressing the
        // recommendations/validate calls that would otherwise hit orders/null/*.
        await $m.order.load_current_order()
    } else {
        $s.cart.loading = false
    }
    return status
}

/**
 * A change to a cart line as its callers know it; `update_cart` completes the request from the line
 * itself. `order_artkey` rides along in the body, although the route takes the order from its path.
 */
type CartItemUpdate = Partial<CartItemWrite> & {order_artkey?: number | null}

/**
 * The write of a cart line. A line whose offer item is gone has neither an offer item artkey nor a
 * price; the backend rejects writing it, and only its delete, which needs neither, goes through.
 */
type CartItemWrite = Omit<ModifyCartItemRequest, 'offer_item_artkey' | 'price_per_case'> & {
    offer_item_artkey: number | null
    price_per_case: ModifyCartItemRequest['price_per_case'] | null
}

/**
 * Update the state of the modified cart, and optionally propagate
 * the updated change to the backend, when the debounce timer is
 * not triggered again. Return whether the cart item was added or modified.
 */
export async function update_cart(
    cart_item_data: CartItemUpdate,
    case_artkey: number,
    debounce_action = true,
): Promise<CartUpdateStatus> {
    // Every add/increase/decrease/remove funnels through here; bounce the cart
    // affordances (top bar, tablet circle, mobile bar) in response.
    trigger_cart_bounce()

    if (!cart_item_data.order_artkey) {
        cart_item_data.order_artkey = $s.cart.artkey
    }
    const cart_item = present($s.cart.items[case_artkey], 'cart item')
    if (typeof cart_item_data.number_of_cases !== 'number') {
        cart_item_data.number_of_cases = cart_item.number_of_cases
    }

    cart_item.number_of_cases = cart_item_data.number_of_cases
    cart_item_data.offer_item_artkey = cart_item.offer_item_artkey
    if (cart_item.custom_offer_item_artkey !== undefined) {
        cart_item_data.custom_offer_item_artkey = cart_item.custom_offer_item_artkey
    }
    cart_item_data.case_artkey = case_artkey

    // Use the default price from the orderitem if no price is passed.
    const price_per_case = cart_item_data.price_per_case || cart_item.list_price
    cart_item_data.price_per_case = price_per_case

    if (cart_item.number_of_cases === 0) {
        logger.debug(`[cart] delete item ${case_artkey} from cart state`)
        // We need this state around, until the api delete has been called.
        cart_item._invisible = true
    } else {
        cart_item._invisible = false
        set_cart_marker(case_artkey)
    }

    $m.order.active_promotion_status()

    CART_LINES_AWAITING_WRITE.add(case_artkey)

    const request: CartItemWrite = {
        ...cart_item_data,
        case_artkey,
        number_of_cases: cart_item.number_of_cases,
        offer_item_artkey: cart_item.offer_item_artkey,
        price_per_case,
    }
    if (!debounce_action || !$s.cart.artkey) {
        return update_cart_item_api(request)
    }
    let debounced = CART_API_DEBOUNCES[case_artkey]
    if (!debounced) {
        debounced = debounce(1000, update_cart_item_api)
        CART_API_DEBOUNCES[case_artkey] = debounced
    }
    return debounced(request)
}

/** Whether a cart change of the customer's own is still debounced or in flight. */
export function has_pending_cart_writes(): boolean {
    return CART_LINES_AWAITING_WRITE.size > 0
}

/**
 * Write a debounced cart change now rather than a second from now. Called when the portal loses
 * focus: the tab the customer switches to reloads the order on arrival, and would otherwise read
 * the change they just made as not having happened.
 */
export function flush_cart_writes() {
    for (const debounced of Object.values(CART_API_DEBOUNCES)) {
        debounced.flush()
    }
}

export async function update_cart_offer_items(clear_offer_items = false) {
    const {result, success} = await api.post(
        'portal/offer-items',
        {
            availabilities: [],
            category_artkeys: [],
            limit: 200,
            offer_item_artkeys: Object.values($s.cart.items)
                .map((i) => i.offer_item_artkey)
                .filter((i) => typeof i === 'number'),
            offer_item_type: OfferItemFilterType.BROWSE_STOCK,
            offset: 0,
            packaging: [],
            search_terms: [],
            sort_ascending: true,
            sort_by: 'entry_date',
        },
        true,
    )

    if (!success) {
        return
    }

    // Make sure to remove old references, or stock amount uses
    // Outdated info after having booked without page reload.
    if (clear_offer_items) {
        for (const key of Object.keys($s.cart.offer_items)) {
            delete $s.cart.offer_items[Number(key)]
        }
    }

    const offer_items = result.portal_offer_items.reduce<Record<number, GetPortalOfferItemResponse>>((a, c) => {
        a[c.case_artkey] = c
        return a
    }, {})
    merge_deep($s.cart.offer_items, offer_items)
}

type CartErrorsByCategory = Partial<Record<CartValidation, Record<number, CartValidationError>>>

export async function validate_cart() {
    // Nothing to validate without an established order; guard against hitting
    // orders/null/validate (404) before the order artkey has been set.
    if (!$s.cart.artkey) {
        return
    }
    // Update offer_items state with the latest data from the API.
    await update_cart_offer_items(false)
    const {result: validation_result, success, status_code} = await api.get(`portal/orders/${$s.cart.artkey}/validate`)

    if (status_code === ORDER_GONE) {
        await $m.order.reload_current_order()
        return
    } else if (!success) {
        notifier.notify($t('notifications.order_failure', {order: `S${$s.cart.artkey}`}), 'danger', 7000)
        return
    }

    if (validation_result.errors.length === 0) {
        // We need to reload the related offer_items, so we don't get stale data
        // From the validation errors mutating the offer_item state.
        reset_validation()
        return
    }
    logger.debug(`[cart] validated cart; errors: ${validation_result.errors.length}`)

    // Map the validation data model to the state model.
    const cart_errors = validation_result.errors.reduce<CartErrorsByCategory>((acc, error) => {
        const valid_value =
            error.validation_error === CartValidation.PRICE_WAS_RAISED
                ? Number.parseFloat(error.valid_value)
                : Number.parseInt(error.valid_value)
        const invalid_value =
            error.validation_error === CartValidation.PRICE_WAS_RAISED
                ? Number.parseFloat(error.invalid_value)
                : Number.parseInt(error.invalid_value)

        const {minimum_quantity} = error
        const {step_size_quantity} = error
        acc[error.validation_error] = {
            ...acc[error.validation_error],
            [error.case_artkey]: {
                ...error,
                invalid_value,
                minimum_quantity,
                step_size_quantity,
                valid_value,
            },
        }

        const cart_items: CartItemLookup = $s.cart.items
        const cart_item = cart_items[error.case_artkey]
        const offer_items: CartOfferItemLookup = $s.cart.offer_items
        const offer_item = offer_items[error.case_artkey]
        if (!cart_item) {
            // The order holds a line this tab doesn't know about yet (another tab added it); the
            // reload on the way back into the portal picks it up.
            return acc
        }

        if ([CartValidation.MIN_QUANTITY, CartValidation.QUANTITY, CartValidation.STEP_SIZE].includes(error.validation_error)) {
            cart_item.value = $m.offer.unit_amount(valid_value, cart_item.case_number_of_bottles)

            if (error.validation_error === CartValidation.QUANTITY) {
                // An out_of_stock item could have an offer_item left in the state,
                // But it is gone after a refresh.
                if (offer_item) {
                    offer_item.list_quantity = valid_value
                }
            }
            if (error.validation_error === CartValidation.MIN_QUANTITY) {
                logger.debug(
                    `[cart] validate minimum quantity: ${cart_item.case_artkey}/${minimum_quantity} ${invalid_value} => ${valid_value}`,
                )
                cart_item.minimum_quantity = minimum_quantity
                if (offer_item) {
                    offer_item.minimum_quantity = minimum_quantity
                }
            }
            if (error.validation_error === CartValidation.STEP_SIZE) {
                logger.debug(
                    `[cart] validate step size: ${cart_item.case_artkey}/${String(step_size_quantity)} ${invalid_value} => ${valid_value}`,
                )
                cart_item.step_size_quantity = step_size_quantity
                if (offer_item) {
                    offer_item.step_size_quantity = step_size_quantity
                }
            }
        }

        return acc
    }, {})
    merge_deep($s.cart.errors, cart_errors)
}

export function vat_booked() {
    return [...$s.cart.sales_order_items, ...$s.cart.sales_order_tbo_items]
        .map((i) => i.number_of_cases * Number(i.price_per_case) * (Number(i.vat_percentage) / 100))
        .reduce((a, b) => a + b, 0)
}

export function vat_cart(validate = false) {
    if (!$s.cart.includes_excise) {
        return 0
    }
    return Object.values($s.cart.items)
        .map((i) => {
            let quantity = i.number_of_cases
            let price_per_case = i.list_price

            if (validate) {
                // Check for quantity validation errors
                const line_quantity_error = quantity_error(i.case_artkey)?.[1]
                if (line_quantity_error) {
                    quantity = line_quantity_error.valid_value
                }

                // Check for price validation errors
                const price_errors: Record<string, CartValidationError | undefined> =
                    $s.cart.errors[CartValidation.PRICE_WAS_RAISED]
                const price_error = price_errors[i.case_artkey]
                if (price_error) {
                    price_per_case = price_error.valid_value
                }
            }

            const total_price = quantity * Number(price_per_case)
            return total_price * (Number(i.vat_percentage) / 100)
        })
        .reduce((a, b) => a + b, 0)
}

export function vat_total(validate = false) {
    if (!$s.cart.includes_excise) {
        return 0
    }
    let total = vat_booked() + vat_cart(validate)
    const voucher = voucher_additional()
    if (voucher?.value_type === 'PERCENTAGE' && voucher.value_per_quantity) {
        total += (total * Number(voucher.value_per_quantity)) / 100
    }

    return total
}

export const vat_total_validated = () => vat_total(true)
export const voucher_additional = () => $s.cart.sales_order_additionals.find((i) => i.sales_order_additional_type === 'voucher')
